import React, { memo, useCallback, useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import ActivityPageLayout from "app/components/Layouts/ActivityPageLayout";
import NotificationsList from "app/components/NotificationsList";
import { useNotificationsModel } from "app/components/NotificationsDropdown/Notifications/indexModel";
import { useTranslation } from "react-i18next";
import { useActions } from "app/utils/hooks";
import { markAllSeen, getTotalUnseen } from "app/actions/Notification";
import no_notifications from "app/images/no_notifications.png";
import { cls } from "app/utils";
import useQueryParams from "app/utils/hooks/useQueryParams";
import DropdownButton from "app/components/DropdownButton";
import blue_checkmark from "app/images/blue_check.svg";
import down_arrow from "app/images/down-chevron.svg";

enum notifQuery {
  coach = "coach",
  athlete = "athlete",
}

const ActivityNotifications = () => {
  const { t } = useTranslation();
  const { setQueryParam, deleteQueryParam, queryParams } = useQueryParams();
  const {
    markAllAsRead,
    notificationsContainer,
    groupedNotifications,
    loading,
    isReading,
    totalUnseen,
    nullData,
  } = useNotificationsModel();

  const notificationAction = useActions({ markAllSeen, getTotalUnseen });

  useEffect(() => {
    if (totalUnseen && totalUnseen?.notifications > 0) {
      notificationAction.markAllSeen();
      notificationAction.getTotalUnseen();
    }
  }, [totalUnseen?.notifications]);

  const onSelectFitler = (filter: string) => {
    setQueryParam("notif_q", filter);
  };

  const deleteQueryFilter = () => {
    deleteQueryParam("notif_q");
  };

  const queryFilters = useMemo(() => {
    return [
      {
        label: t("All"),
        mobileLabel: t("All notifications"),
        value: null,
        onClick: () => {
          deleteQueryFilter();
        },
      },
      {
        label: t("By Coaches"),
        value: notifQuery.coach,
        onClick: () => {
          onSelectFitler(notifQuery.coach);
        },
      },
      {
        label: t("By Athletes"),
        value: notifQuery.athlete,
        onClick: () => {
          onSelectFitler(notifQuery.athlete);
        },
      },
    ];
  }, []);

  const NullData = useCallback(() => {
    return (
      <div className={styles.empty_container}>
        <img src={no_notifications} alt="no invites" />
        <div className={styles.empty_text_wrapper}>
          <p className={styles.empty_text}>{t("No Notifications")}</p>
          <p className={styles.emtpy_text_description}>
            {t("You don’t have any notifications yet")}
          </p>
        </div>
      </div>
    );
  }, []);

  const DropdownFilterButton = useCallback(() => {
    const selectedFilterLabel = queryFilters.find(
      (a) => a.value === queryParams.get("notif_q"),
    );
    return (
      <button className={styles.dropdown_btn}>
        <p className={styles.dropdown_label}>
          {selectedFilterLabel?.mobileLabel ?? selectedFilterLabel.label}
        </p>
        <img
          src={down_arrow}
          alt="dropdown_icon"
          className={styles.dropdown_arrow}
        />
      </button>
    );
  }, [queryParams?.get("notif_q")]);

  return (
    <ActivityPageLayout>
      <div className={styles.panel}>
        <div className={styles.desktop_filters_container}>
          {queryFilters.map((filter) => {
            return (
              <button
                key={filter.label}
                disabled={filter.value === queryParams.get("notif_q")}
                className={cls(styles.filter_btn_wrapper, {
                  [styles.selected_filter_btn]:
                    filter.value === queryParams.get("notif_q"),
                })}
                onClick={filter.onClick}
              >
                <p className={styles.filter_btn_label}>{filter.label}</p>
              </button>
            );
          })}
        </div>
        <div className={styles.mobile_filters_container}>
          <DropdownButton
            closeOnClick
            btnContent={<DropdownFilterButton />}
            dropdownClassname={styles.dropdown_container}
            dropdownWrapperClass={styles.dropdown_wrapper}
          >
            {queryFilters.map((filter) => {
              const selectedFitler =
                filter.value === queryParams.get("notif_q");

              return (
                <button
                  key={filter.label}
                  className={cls(styles.drop_filter_btn_wrapper, {
                    [styles.drop_selected_filter_btn]: selectedFitler,
                  })}
                  onClick={filter.onClick}
                >
                  <p className={styles.filter_btn_label}>
                    {filter?.mobileLabel ?? filter.label}
                  </p>
                  {selectedFitler && <img src={blue_checkmark} />}
                </button>
              );
            })}
          </DropdownButton>
        </div>
        <span
          className={styles.icon_wrapper}
          onClick={!isReading && markAllAsRead}
        >
          <i className="btn ico ico-read" />
          <p className={styles.read_all}>{t("Read All")}</p>
        </span>
      </div>
      <NotificationsList
        containerClassName={styles.notifications_list}
        nullData={nullData}
        data={groupedNotifications}
        notificationsContainer={notificationsContainer}
        loading={loading || isReading}
        noDataChildren={<NullData />}
      />
    </ActivityPageLayout>
  );
};

export default memo(ActivityNotifications);
